import { _ } from "libs";

import React from "react";
import { Club } from "../../types";
import moment from "moment";
import ClubGallery from "./clubGallery";

type OpeningDay = {
	firstDay: number | null;
	lastDay: number | null;
	opening: number | null;
	closing: number | null;
};

function newBuffer(firstDay: number, opening: number, closing: number): OpeningDay {
	return {
		firstDay,
		lastDay: firstDay,
		opening,
		closing,
	};
}

function collapseOpeningHours(openingHours): OpeningDay[] {
	let result: OpeningDay[] = [];

	//day 0 in array is 1 = sunday
	openingHours[7] = openingHours[0];
	const firstDay = openingHours[1];

	let buffer: OpeningDay = newBuffer(firstDay.dayOfWeek, firstDay.opening / 60 / 60, firstDay.closing / 60 / 60);

	for (let i = 1; i <= openingHours.length - 1; i++) {
		const { dayOfWeek, opening, closing } = openingHours[i];
		const openingHour = opening / 60 / 60;
		const closingHour = closing / 60 / 60;

		if (openingHour !== buffer.opening || closingHour !== buffer.closing) {
			result.push(buffer);
			buffer = newBuffer(dayOfWeek, openingHour, closingHour);
		} else {
			buffer.lastDay = dayOfWeek;
		}
	}
	result.push(buffer);

	return result;
}

function getCorrectDay(number: number): number {
	if (number === 1) {
		return 7;
	}

	return number - 1;
}

function formatHour(hour: number, format = "k:mm"): string {
	const duration = moment.duration(hour, "hours");
	return moment({ hour: duration.hours(), minute: duration.minutes() }).format(format);
}

function PrivateClubMembership({
	club,
	onRequestMembershipClicked,
	onCancelClubMembershipClicked,
}: {
	club: Club;
	onRequestMembershipClicked: (clubId: string) => void;
	onCancelClubMembershipClicked: Function;
}) {
	let info =
		club.clubDetails.type === "mixed" ? _("club.mixedAndPlayerNotMember") : _("club.privateAndPlayerNotMember");

	if (["not_member", "request_declined"].indexOf(club.clubDetails.memberStatus) >= 0) {
		return (
			<div className="claimClubMembership">
				<div className="group">
					<div className="msg info">{info}</div>
				</div>
				<div className="group">
					<button
						className="button fullWidth"
						onClick={() => {
							onRequestMembershipClicked(club.clubInfo.clubId);
						}}
					>
						{_("club.claimMembershipBtn")}
					</button>
				</div>
			</div>
		);
	}

	if (club.clubDetails.memberStatus === "request_pending") {
		return (
			<div className="pendingClubMembership">
				<div className="group">
					<div className="msg info">{info}</div>
				</div>
				<div className="group">
					<p>
						{_("club.pendingClubMembership")}
						<br />
						<span
							className="link"
							onClick={() => {
								onCancelClubMembershipClicked(club.clubInfo.clubId);
							}}
						>
							{_("club.cancelMembershipRequestBtn")}
						</span>
					</p>
				</div>
			</div>
		);
	}
}

const ClubAbout = ({
	club,
	onRequestMembershipClicked,
	onCancelClubMembershipClicked,
}: {
	club: Club;
	onRequestMembershipClicked: (clubId: string) => void;
	onCancelClubMembershipClicked: Function;
}) => {
	if (club?.clubInfo?.migratedURL) {
		return (
			<div className="clubContent text">
				<div className="contentLeft">
					<div className="group">
						<h6>
							{_("club.about.heading")} {club.clubInfo.name}
						</h6>
						<div
							className="msg info"
							dangerouslySetInnerHTML={{
								__html: _("club.about.migrated", null, { url: club.clubInfo.migratedURL }),
							}}
						></div>
					</div>
				</div>
			</div>
		);
	}
	const { clubInfo, clubDetails } = club;
	const memberStatus = club.clubDetails.memberStatus;
	const clubPrivateButNotMember =
		club.clubDetails.type === "private" &&
		["not_member", "request_pending", "request_declined"].indexOf(memberStatus) >= 0;
	const clubMixedButNotMember =
		club.clubDetails.type === "mixed" &&
		["not_member", "request_pending", "request_declined"].indexOf(memberStatus) >= 0;
	let clubWeb = clubInfo.web;
	if (!/^https?:\/\//i.test(clubWeb)) {
		clubWeb = "http://" + clubWeb;
	}
	return (
		<div className="clubContent text">
			<div className="contentLeft">
				<div className="group">
					<h6>
						{_("club.about.heading")} {club.clubInfo.name}
					</h6>
					{club.clubInfo.contractedClub ? (
						<div className="text" dangerouslySetInnerHTML={{ __html: clubInfo.aboutText }} />
					) : (
						<div className="msg info">{_("club.nonContractedDescription")}</div>
					)}
				</div>
				{club.clubInfo.contractedClub && clubInfo.termsText ? (
					<>
						<div className="group">
							<h6>{_("club.terms.heading")}</h6>
							<div className="text" dangerouslySetInnerHTML={{ __html: clubInfo.termsText }} />
						</div>
					</>
				) : null}
			</div>
			<div className="contentRight">
				{clubPrivateButNotMember ? (
					<PrivateClubMembership
						club={club}
						onRequestMembershipClicked={onRequestMembershipClicked}
						onCancelClubMembershipClicked={onCancelClubMembershipClicked}
					/>
				) : null}
				{clubMixedButNotMember ? (
					<PrivateClubMembership
						club={club}
						onRequestMembershipClicked={onRequestMembershipClicked}
						onCancelClubMembershipClicked={onCancelClubMembershipClicked}
					/>
				) : null}
				<ClubGallery photos={clubDetails.photos} />
				<div className="group">
					<h6>{_("club.address")}</h6>
					<p>
						{clubInfo.address1}
						<br />
						{clubInfo.city}
					</p>
				</div>
				{clubDetails.openingHours ? (
					<div className="group">
						<h6>{_("club.openingHours")}</h6>
						<ul>
							{collapseOpeningHours(clubDetails.openingHours).map(
								({ firstDay, lastDay, opening, closing }) => (
									<li key={`${firstDay}_${lastDay}`}>
										{firstDay === lastDay ? (
											moment(getCorrectDay(firstDay), "E").format("ddd")
										) : (
											<span>
												{moment(getCorrectDay(firstDay), "E").format("ddd")} -{" "}
												{moment(getCorrectDay(lastDay), "E").format("ddd")}
											</span>
										)}
										: {opening ? formatHour(opening) : ""} - {closing ? formatHour(closing) : ""}
									</li>
								)
							)}
						</ul>
					</div>
				) : null}
				<div className="group">
					<h6>{_("club.contact")}</h6>
					<ul>
						{clubInfo.mobilePhone && clubInfo.mobilePhone !== " " ? (
							<li>
								{_("club.contact.phone")}: {clubInfo.mobilePhone}
							</li>
						) : null}
						{clubInfo.email ? (
							<li>
								{_("club.contact.email")}:&nbsp;
								{clubInfo.email ? (
									<a href={`mailto:${clubInfo.email}`} className="link">
										{clubInfo.email}
									</a>
								) : null}
							</li>
						) : null}
						{clubInfo.web ? (
							<li>
								{_("club.contact.web")}:&nbsp;
								{clubInfo.web ? (
									<a href={clubWeb} className="link" target="_blank" rel="noopener noreferrer">
										{clubInfo.web}
									</a>
								) : null}
							</li>
						) : null}
					</ul>
				</div>
				{clubDetails.clubPerks && clubDetails.clubPerks.length > 0 ? (
					<div className="group">
						<h6>{_("club.infrastructure")}</h6>
						<div className="clubPerks">
							{clubDetails.clubPerks.map(({ perkId, name }) => (
								<div className="clubPerk" data-perk={name} key={perkId}>
									<div className="icon" />
									<div className="name">{_("club.perk." + name)}</div>
								</div>
							))}
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default ClubAbout;
